import classNames from 'classnames';
import PropTypes from 'prop-types';
import alertIcon from '../../icons/Icon-Red-Alert-Circle.svg';
import './InlineErrorMessage.scss';

/**
 * InlineErrorMessage Component
 *
 * The red-outlined error callout from the #737 design: a circled 「!」 followed
 * by one semibold line of guidance, boxed in a 1px red border on white.
 *
 * Shared rather than per-surface because #737 puts the same box in four
 * places — the hashtag sheet's "narrowed to zero" and "failed to load" states
 * (AC C20108247 / C20108248) and the add-to-Market-Board modal's board-list
 * and registration failures (AC C20108250 / C20108251) — and they have to keep
 * looking alike as the design moves. Deliberately NOT used for the questions
 * step's zero-match warning (AC C20108249), which keeps its own yellow
 * treatment; see `.ai-scanner-questions-warning`.
 *
 * `role="alert"` rather than the `role="status"` region that step uses: this
 * box is mounted in response to something the member just did (a tap, a
 * request that failed) rather than sitting idle waiting for text, and `alert`
 * is announced on insertion. Callers therefore mount it conditionally instead
 * of keeping an empty one in the tree.
 *
 * Positioning is the caller's — pass `className` and give it the margin the
 * surrounding layout needs. This component only owns the box itself.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.message - The already-localized message.
 * @param {string} [props.className] - Class for the surface's own positioning.
 * @param {string} [props.id] - So a surface can point a control's
 *   `aria-describedby` at this box. `role="alert"` only fires once, on
 *   insertion; a member who reaches the control afterwards — by tabbing to it,
 *   or by re-entering the dialog — gets nothing without the association. Pass
 *   an id from `useId` rather than a literal: two instances of a surface can
 *   be mounted at once, and a duplicate id would point both at the same box.
 * @returns {JSX.Element}
 */
export const InlineErrorMessage = ({ message, className, id }) => (
  <p
    className={classNames('inline-error-message', className)}
    id={id}
    role="alert"
  >
    <img
      className="inline-error-message-icon"
      src={alertIcon}
      alt=""
      aria-hidden="true"
      width="20"
      height="20"
    />
    <span className="inline-error-message-text">{message}</span>
  </p>
);

InlineErrorMessage.propTypes = {
  message: PropTypes.node.isRequired,
  className: PropTypes.string,
  id: PropTypes.string
};

export default InlineErrorMessage;
